import { useCallback, useEffect, useState } from 'react'
import { api, ErroApi } from '../lib/api'
import type { PacoteCatalogo, Procedimento, Lista } from '../lib/api'
import { Secao, Vazio } from '../componentes/ui'
import { brl } from '../lib/formato'
import './pacotes-catalogo.css'

/**
 * O catálogo de pacotes.
 *
 * ─── O QUE FALTAVA ────────────────────────────────────────────────
 *
 * A tabela `public.pacote` existe desde a 0008 e nunca teve rota
 * nenhuma. Na prática cada venda redigitava nome, número de sessões e
 * preço — e duas vendas do "mesmo" pacote saíam com valores
 * diferentes, o que só aparece no fim do mês, no relatório.
 *
 * ─── POR QUE ISTO É UM CADASTRO, E NÃO UM CAMPO ───────────────────
 *
 * Pacote carrega três regras que a clínica decide UMA vez e não quer
 * repensar em cada venda:
 *
 *  · quantas sessões, e por quanto;
 *  · quanto tempo vale (validade), porque sessão sem prazo vira
 *    passivo eterno — o paciente some por dois anos e volta cobrando;
 *  · **se a falta queima a sessão**, que é a regra que gera briga no
 *    balcão e por isso tem de estar escrita antes de acontecer.
 *
 * A venda FOTOGRAFA tudo isso (0037). Reajustar o pacote de R$ 1.200
 * para R$ 1.400 não reescreve quem já comprou.
 */
export function PacotesCatalogo({ podeEditar }: { podeEditar: boolean }) {
  const [itens, setItens] = useState<PacoteCatalogo[]>([])
  const [procedimentos, setProcedimentos] = useState<Procedimento[]>([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  /** `false` fechado · `null` novo · objeto editando. */
  const [form, setForm] = useState<false | null | PacoteCatalogo>(false)
  const [verInativos, setVerInativos] = useState(false)

  const carregar = useCallback(async () => {
    try {
      const [p, pr] = await Promise.all([
        api<{ itens: PacoteCatalogo[] }>(`/v1/pacotes?incluir_inativos=${verInativos}`),
        api<Lista<Procedimento>>('/v1/procedimentos?limite=100').catch(() => null),
      ])
      setItens(p.itens)
      setProcedimentos(pr?.itens ?? [])
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não foi possível carregar os pacotes.')
    } finally { setCarregando(false) }
  }, [verInativos])
  useEffect(() => { void carregar() }, [carregar])

  const ativos = itens.filter((p) => p.ativo).length

  return (
    <>
      <Secao
        titulo="Pacotes"
        descricao="Sessões vendidas em bloco. O saldo cai sozinho a cada atendimento concluído."
        veredito={ativos > 0
          ? { texto: `${ativos} em uso`, tom: 'ok' }
          : { texto: 'nenhum', tom: 'neutro' }}
        acao={podeEditar && (
          <button className="botao botao--pequeno" onClick={() => setForm(null)}>
            Novo pacote
          </button>
        )}
      >
        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        {carregando ? (
          <div className="esqueleto" style={{ height: 90 }} />
        ) : itens.length === 0 ? (
          <Vazio
            mensagem="Nenhum pacote no catálogo."
            sugestao="Pacote é quando o paciente paga várias sessões de uma vez — dez de fisioterapia, por exemplo. Cadastrado aqui, ele aparece na hora de vender e o saldo desconta sozinho."
            acao={podeEditar && (
              <button className="botao" onClick={() => setForm(null)}>
                Cadastrar o primeiro
              </button>
            )}
          />
        ) : (
          <ul className="pcat__lista">
            {itens.map((p) => {
              const proc = procedimentos.find((x) => x.id === p.procedimento_id)
              const unit = Number(p.valor_centavos) / p.sessoes
              return (
                <li key={p.id} className={`pcat__item${p.ativo ? '' : ' is-inativo'}`}>
                  <div className="pcat__id">
                    <strong className="pcat__nome">{p.nome}</strong>
                    <span className="pcat__linha">
                      <span className="pcat__sessoes">{p.sessoes} sessões</span>
                      <span className="pcat__valor">{brl(p.valor_centavos)}</span>
                      <span className="ajuda">{brl(unit)} cada</span>
                    </span>
                  </div>

                  {/* Chip com BORDA = atributo. O que é ESTADO ("fora
                      de uso") ganha pílula tingida. */}
                  <div className="pcat__chips">
                    {proc && <span className="pcat__chip">{proc.nome}</span>}
                    <span className="pcat__chip">
                      {p.validade_dias ? `vale ${p.validade_dias} dias` : 'sem prazo'}
                    </span>
                    <span className="pcat__chip">
                      {p.no_show_consome ? 'falta queima a sessão' : 'falta não queima'}
                    </span>
                    {(p.vendidos ?? 0) > 0 && (
                      <span className="pcat__chip">{p.vendidos} vendido{p.vendidos === 1 ? '' : 's'}</span>
                    )}
                    {!p.ativo && <span className="selo selo--neutro">fora de uso</span>}
                  </div>

                  {podeEditar && (
                    <button className="botao botao--fantasma botao--pequeno"
                            onClick={() => setForm(p)}>Editar</button>
                  )}
                </li>
              )
            })}
          </ul>
        )}

        {/* O filtro mora sob a seção, não numa barra global — §6 das
            referências: cada seção filtra a si mesma. */}
        {itens.some((p) => !p.ativo) || verInativos ? (
          <label className="pcat__filtro">
            <input type="checkbox" checked={verInativos}
                   onChange={(e) => setVerInativos(e.target.checked)} />
            <span>Mostrar também os que saíram de uso</span>
          </label>
        ) : null}
      </Secao>

      {form !== false && (
        <FormPacote pacote={form} procedimentos={procedimentos}
                    aoFechar={() => setForm(false)}
                    aoSalvar={() => { setForm(false); void carregar() }} />
      )}
    </>
  )
}

function FormPacote({ pacote, procedimentos, aoFechar, aoSalvar }: {
  pacote: PacoteCatalogo | null
  procedimentos: Procedimento[]
  aoFechar(): void
  aoSalvar(): void
}) {
  const editando = pacote !== null
  const [nome, setNome] = useState(pacote?.nome ?? '')
  const [sessoes, setSessoes] = useState(pacote?.sessoes ?? 10)
  const [valor, setValor] = useState(
    pacote ? String(Number(pacote.valor_centavos) / 100).replace('.', ',') : '')
  const [procedimentoId, setProcedimentoId] = useState(pacote?.procedimento_id ?? '')
  const [temPrazo, setTemPrazo] = useState(pacote ? pacote.validade_dias !== null : true)
  const [validade, setValidade] = useState(pacote?.validade_dias ?? 120)
  const [noShow, setNoShow] = useState(pacote?.no_show_consome ?? true)
  const [ativo, setAtivo] = useState(pacote?.ativo ?? true)
  const [erro, setErro] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    const noEscape = (e: KeyboardEvent) => { if (e.key === 'Escape') aoFechar() }
    document.addEventListener('keydown', noEscape)
    return () => document.removeEventListener('keydown', noEscape)
  }, [aoFechar])

  // Reais na tela, centavos no banco. Float em dinheiro é como R$ 0,01
  // some do fechamento e ninguém acha de onde.
  const centavos = Math.round(Number(valor.replace(',', '.') || 0) * 100)
  const unit = sessoes > 0 ? centavos / sessoes : 0

  /* Preenche o nome sozinho a partir do procedimento, até a pessoa
     mexer. É o campo que ela ia digitar igual de qualquer jeito. */
  const escolherProcedimento = (id: string) => {
    setProcedimentoId(id)
    const p = procedimentos.find((x) => x.id === id)
    if (p && !nome.trim()) setNome(`${sessoes} sessões de ${p.nome}`)
  }

  const valido = nome.trim().length >= 2 && sessoes >= 2 && centavos >= 0

  async function submeter(e: React.FormEvent) {
    e.preventDefault()
    if (!valido) return
    setEnviando(true); setErro(null)
    try {
      const corpo = {
        nome: nome.trim(),
        sessoes,
        valor_centavos: centavos,
        procedimento_id: procedimentoId || null,
        validade_dias: temPrazo ? validade : null,
        no_show_consome: noShow,
        ...(editando ? { ativo } : {}),
      }
      await api(editando ? `/v1/pacotes/${pacote.id}` : '/v1/pacotes',
                { metodo: editando ? 'PATCH' : 'POST', corpo })
      aoSalvar()
    } catch (err) {
      setErro(err instanceof ErroApi ? err.message : 'Não foi possível salvar.')
      setEnviando(false)
    }
  }

  return (
    <>
      <div className="painel__fundo" onClick={aoFechar} />
      <aside className="painel pcat__form" role="dialog" aria-modal="true"
             aria-label={editando ? 'Editar pacote' : 'Novo pacote'}>
        <header className="painel__topo">
          <h2>{editando ? 'Editar pacote' : 'Novo pacote'}</h2>
          <button className="botao botao--fantasma botao--pequeno" onClick={aoFechar}>
            Fechar
          </button>
        </header>

        {erro && <div className="aviso aviso--erro" role="alert">{erro}</div>}

        <form className="formulario" onSubmit={submeter}>
          <div className="campo">
            <label htmlFor="pk-proc">Do que é o pacote</label>
            <select id="pk-proc" className="entrada" value={procedimentoId}
                    onChange={(e) => escolherProcedimento(e.target.value)}>
              <option value="">Qualquer atendimento</option>
              {procedimentos.filter((p) => p.ativo).map((p) => (
                <option key={p.id} value={p.id}>{p.nome}</option>
              ))}
            </select>
            <span className="ajuda">
              Amarrar a um procedimento faz o sistema oferecer o pacote na hora
              de marcar aquele atendimento.
            </span>
          </div>

          <div className="campo">
            <label htmlFor="pk-nome">Como aparece na venda</label>
            <input id="pk-nome" className="entrada" required maxLength={120}
                   placeholder="10 sessões de fisioterapia"
                   value={nome} onChange={(e) => setNome(e.target.value)} />
          </div>

          <div className="pcat__dupla">
            <div className="campo">
              <label htmlFor="pk-ses">Quantas sessões</label>
              <input id="pk-ses" className="entrada" type="number" min={2} max={200} required
                     value={sessoes} onChange={(e) => setSessoes(Number(e.target.value))} />
            </div>
            <div className="campo">
              <label htmlFor="pk-val">Preço do pacote (R$)</label>
              <input id="pk-val" className="entrada" inputMode="decimal" required
                     placeholder="1200,00"
                     value={valor} onChange={(e) => setValor(e.target.value)} />
            </div>
          </div>

          {/* O número que decide a venda: quanto sai cada sessão. É a
              conta que a recepção faz de cabeça para convencer, e
              deixá-la à vista evita o erro de digitar o preço unitário
              no campo do pacote. */}
          {centavos > 0 && sessoes > 0 && (
            <p className="pcat__conta">
              Sai a <strong>{brl(unit)}</strong> por sessão.
            </p>
          )}

          <fieldset className="pcat__regras">
            <legend>As duas regras que geram discussão no balcão</legend>

            <label className="pcat__caixa">
              <input type="checkbox" checked={temPrazo}
                     onChange={(e) => setTemPrazo(e.target.checked)} />
              <span>
                Tem prazo para usar
                <small className="ajuda">
                  Sem prazo, a sessão vira passivo eterno: o paciente some por
                  dois anos e volta cobrando.
                </small>
              </span>
            </label>
            {temPrazo && (
              <div className="campo pcat__prazo">
                <label htmlFor="pk-prazo">Vale por quantos dias</label>
                <input id="pk-prazo" className="entrada" type="number" min={1} max={3650}
                       value={validade} onChange={(e) => setValidade(Number(e.target.value))} />
                <span className="ajuda">Contados a partir da compra.</span>
              </div>
            )}

            <label className="pcat__caixa">
              <input type="checkbox" checked={noShow}
                     onChange={(e) => setNoShow(e.target.checked)} />
              <span>
                Faltar sem avisar queima a sessão
                <small className="ajuda">
                  Desmarcado, a falta devolve a sessão para a fila e o paciente
                  remarca. A regra fica gravada na venda — mudar aqui não
                  atinge quem já comprou.
                </small>
              </span>
            </label>
          </fieldset>

          {editando && (
            <label className="pcat__caixa">
              <input type="checkbox" checked={ativo}
                     onChange={(e) => setAtivo(e.target.checked)} />
              <span>
                Ainda vendendo
                <small className="ajuda">
                  Desmarcado, some da hora de vender. Quem já comprou continua
                  com o saldo intacto.
                </small>
              </span>
            </label>
          )}

          <button className="botao" type="submit" disabled={!valido || enviando}>
            {enviando ? 'Salvando…' : editando ? 'Salvar' : 'Cadastrar pacote'}
          </button>
        </form>
      </aside>
    </>
  )
}
